import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import BottomNav from './BottomNav';
import NotificationSystem from './NotificationSystem';
import api from '../api/client';
import webSocketEventBus from '../services/webSocketEventBus';
import {
  Stethoscope, Settings, LogOut, Shield, Plus, HelpCircle,
  Home as HomeIcon, Activity, Heart, Users, FileText,
  UserCircle, MessageSquare, UserCheck, Lock, Clock,
  BarChart3, ChevronLeft, UserCog, ShieldCheck, Menu, X, Search, Loader2, Plug, LayoutGrid
} from 'lucide-react';

/**
 * AppLayout — Single unified layout for ALL authenticated pages.
 * 
 * Provides:
 *  - Desktop sidebar with role-based navigation (each item = its own route)
 *  - Mobile top bar with centered title + optional back button
 *  - Desktop top bar with Help / Notifications / Profile
 *  - Mobile bottom nav
 *  - Consistent content area with overflow protection
 */
const AppLayout = ({ children, title, showBack = false, rightAction = null }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const role = user?.role; // 'USER' | 'DOCTOR' | 'ADMIN'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = React.useRef(null);
  const [badgeCounts, setBadgeCounts] = useState({ total: 0, messages: 0 });
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [doctorCounts, setDoctorCounts] = useState({ patientLog: 0, requests: 0, unread: 0 });
  const [adminCounts, setAdminCounts] = useState({ pendingDoctors: 0 });

  useEffect(() => {
    const handler = (counts) => setBadgeCounts(counts);
    webSocketEventBus.on('unreadCounts', handler);
    return () => webSocketEventBus.off('unreadCounts', handler);
  }, []);

  useEffect(() => {
    if (role !== 'USER') return;
    const fetchPending = async () => {
      try {
        const res = await api.get('/care-requests/my-requests?status=pending').catch(() => null);
        const count = Array.isArray(res?.data?.data) ? res.data.data.length : (res?.data?.stats?.pending || 0);
        setPendingRequestsCount(count);
      } catch { /* non-fatal */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [role]);

  useEffect(() => {
    if (role !== 'DOCTOR') return;
    const fetchDoctorCounts = async () => {
      try {
        const [symptomsRes, requestsRes, unreadRes] = await Promise.all([
          api.get('/symptoms?status=OPEN&limit=1').catch(() => null),
          api.get('/care-requests/sent-requests?status=pending&limit=1').catch(() => null),
          api.get('/profile/notifications/unread-count').catch(() => null),
        ]);
        setDoctorCounts({
          patientLog: symptomsRes?.data?.pagination?.total ?? symptomsRes?.data?.total ?? 0,
          requests: Array.isArray(requestsRes?.data?.data)
            ? requestsRes.data.data.filter(r => r.status === 'pending').length
            : 0,
          unread: unreadRes?.data?.count ?? 0,
        });
      } catch { /* non-fatal */ }
    };
    fetchDoctorCounts();
    const interval = setInterval(fetchDoctorCounts, 30000);
    return () => clearInterval(interval);
  }, [role]);

  // Bump unread badge when a new symptom arrives via WebSocket
  useEffect(() => {
    if (role !== 'DOCTOR') return;
    const handler = () => setDoctorCounts(prev => ({ ...prev, unread: prev.unread + 1 }));
    webSocketEventBus.on('newSymptom', handler);
    webSocketEventBus.on('new_patient_report', handler);
    return () => {
      webSocketEventBus.off('newSymptom', handler);
      webSocketEventBus.off('new_patient_report', handler);
    };
  }, [role]);

  // Clear unread badge when doctor visits Patient Log — marks all patient-report notifications as read
  useEffect(() => {
    if (role !== 'DOCTOR' || location.pathname !== '/health-log') return;
    setDoctorCounts(prev => ({ ...prev, unread: 0 }));
    api.patch('/profile/notifications/read-all?type=new_patient_report').catch(() => {});
  }, [role, location.pathname]);

  // Decrement unread badge when doctor opens any symptom or care-event, from any page
  useEffect(() => {
    if (role !== 'DOCTOR') return;
    const careEventMatch = location.pathname.match(/^\/care-event\/([a-f0-9]{24})$/i);
    const symptomMatch   = location.pathname.match(/^\/symptom\/([a-f0-9]{24})$/i);
    if (!careEventMatch && !symptomMatch) return;

    const body = careEventMatch
      ? { careEventId: careEventMatch[1] }
      : { symptomId: symptomMatch[1] };

    api.patch('/profile/notifications/mark-read-by-context', body)
      .then(() => api.get('/profile/notifications/unread-count'))
      .then(res => {
        const count = res?.data?.count ?? 0;
        setDoctorCounts(prev => ({ ...prev, unread: count }));
      })
      .catch(() => {});
  }, [role, location.pathname]);

  useEffect(() => {
    if (role !== 'ADMIN') return;
    const fetchAdminCounts = async () => {
      try {
        const res = await api.get('/admin/doctors?status=PENDING').catch(() => null);
        setAdminCounts({ pendingDoctors: res?.data?.data?.length ?? 0 });
      } catch { /* non-fatal */ }
    };
    fetchAdminCounts();
    const interval = setInterval(fetchAdminCounts, 120000);
    return () => clearInterval(interval);
  }, [role]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query || query.length < 2) { setSearchResults(null); return; }
    searchTimeout.current = setTimeout(async () => {
      if (role !== 'DOCTOR') return;
      setSearchLoading(true);
      try {
        const res = await api.get(`/symptoms/search?q=${encodeURIComponent(query)}&limit=10`);
        if (res.data?.success) setSearchResults(res.data.data);
      } catch (err) { console.error('Search error:', err); }
      finally { setSearchLoading(false); }
    }, 300);
  };

  const handleLogout = () => {
    logout();
    navigate('/welcome');
  };

  // ── Role-based sidebar navigation ──────────────────────────
  // Each item maps to a real route. No more section switching.
  const patientNav = [
    { path: '/dashboard',      icon: HomeIcon,       label: 'Home' },
    { path: '/my-health',      icon: Activity,       label: 'My Health' },
    { path: '/care-team',      icon: Users,          label: 'Care Team' },
    { path: '/care-requests',  icon: UserCheck,      label: 'Requests' },
    { path: '/messages',       icon: MessageSquare,  label: 'Messages' },
    { path: '/my-data',        icon: Lock,           label: 'My Data' },
    { path: '/settings',       icon: Settings,       label: 'Settings' },
  ];

  const doctorNav = [
    { path: '/doctor-dashboard', icon: HomeIcon,       label: 'Home' },
    { path: '/health-log',      icon: Activity,       label: 'Patient Log' },
    { path: '/population',      icon: LayoutGrid,     label: 'Patient Panel' },
    { path: '/schedule',        icon: Clock,          label: 'Schedule' },
    { path: '/care-team',       icon: Users,          label: 'Patients' },
    { path: '/care-requests',   icon: UserCheck,      label: 'Requests' },
    { path: '/priority-queue',  icon: Shield,         label: 'Priority Queue' },
    { path: '/messages',        icon: MessageSquare,  label: 'Messages' },
    { path: '/settings',        icon: Settings,       label: 'Settings' },
  ];

  const adminNav = [
    { path: '/admin',              icon: HomeIcon,       label: 'Dashboard' },
    { path: '/admin/admins',       icon: ShieldCheck,    label: 'Admin Accounts' },
    { path: '/admin/doctors',      icon: Stethoscope,    label: 'Doctor Management' },
    { path: '/admin/users',        icon: UserCog,        label: 'User Management' },
    { path: '/admin/on-call',      icon: Clock,          label: 'On-Call Schedule' },
    { path: '/admin/population',   icon: BarChart3,      label: 'Population Health' },
    { path: '/admin/integrations', icon: Plug,           label: 'Integrations' },
    { path: '/admin/settings',     icon: Settings,       label: 'System Settings' },
  ];

  const navItems = role === 'ADMIN' ? adminNav : role === 'DOCTOR' ? doctorNav : patientNav;

  // Is a nav item active?
  const isNavActive = (path) => {
    if (location.pathname === path) return true;
    // /admin should NOT match /admin/doctors etc.
    if (path !== '/' && path !== '/admin' && location.pathname.startsWith(path + '/')) return true;
    return false;
  };

  // Sidebar CTA
  const sidebarCTA = role === 'DOCTOR' ? {
    label: 'Priority Queue', icon: Shield, path: '/priority-queue'
  } : role === 'USER' ? {
    label: 'New Assessment', icon: Plus, path: '/report'
  } : null;

  // Portal label
  const portalLabel = role === 'ADMIN' ? 'Admin Panel' : role === 'DOCTOR' ? 'Doctor Portal' : 'Patient Portal';
  const roleLabel = role === 'DOCTOR' ? 'Practitioner' : role === 'ADMIN' ? 'Administrator' : 'Patient';

  return (
    <div className="min-h-screen flex bg-surface w-full overflow-x-hidden">
      {/* Skip to content — accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-xl focus:text-sm focus:font-bold"
      >
        Skip to main content
      </a>
      {/* ─── SIDEBAR (Desktop only) ──────────────────────────────── */}
      <aside
        className="hidden lg:flex flex-col h-screen w-64 border-r border-outline-variant/10 bg-surface-container-high fixed left-0 top-0 z-40 p-4 gap-2"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-lg font-black text-primary tracking-tighter">SymptoConnect</div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {portalLabel}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col gap-1" aria-label="Sidebar">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                aria-current={active ? 'page' : undefined}
                aria-label={item.label}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                  active
                    ? 'bg-white text-primary font-semibold shadow-sm'
                    : 'text-slate-500 hover:bg-white/50'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
                {item.path === '/health-log' && role === 'DOCTOR' && doctorCounts.unread > 0 && (
                  <span className="ml-auto bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {doctorCounts.unread > 99 ? '99+' : doctorCounts.unread}
                  </span>
                )}
                {item.path === '/messages' && badgeCounts.messages > 0 && (
                  <span className="ml-auto bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {badgeCounts.messages > 99 ? '99+' : badgeCounts.messages}
                  </span>
                )}
                {item.path === '/care-requests' && role === 'USER' && pendingRequestsCount > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                  </span>
                )}
                {item.path === '/health-log' && doctorCounts.patientLog > 0 && (
                  <span className="ml-auto bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {doctorCounts.patientLog > 99 ? '99+' : doctorCounts.patientLog}
                  </span>
                )}
                {item.path === '/care-requests' && role === 'DOCTOR' && doctorCounts.requests > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {doctorCounts.requests > 99 ? '99+' : doctorCounts.requests}
                  </span>
                )}
                {item.path === '/admin/doctors' && adminCounts.pendingDoctors > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                    {adminCounts.pendingDoctors > 99 ? '99+' : adminCounts.pendingDoctors}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom: CTA + User card */}
        <div className="mt-auto border-t border-slate-200 pt-4 flex flex-col gap-4">
          {sidebarCTA && (
            <button
              onClick={() => navigate(sidebarCTA.path)}
              className="w-full bg-primary-container text-white rounded-xl py-3 px-4 flex items-center justify-center gap-2 font-bold shadow-md hover:shadow-lg active:scale-95 transition-all"
            >
              <sidebarCTA.icon className="w-4 h-4" />
              {sidebarCTA.label}
            </button>
          )}
          <div className="flex items-center gap-3 px-4 py-2 bg-surface-container-low rounded-xl">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=00478d&color=fff&size=40`}
              alt={user?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-on-surface leading-tight break-words">{user?.name || 'User'}</div>
              <div className="text-xs text-slate-500 font-medium">{roleLabel}</div>
            </div>
            <button onClick={handleLogout} className="text-slate-500 hover:text-error transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded" aria-label="Log out">
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      {/* ─── MAIN CONTENT AREA ───────────────────────────────────── */}
      <main className="flex-1 lg:ml-64 flex flex-col min-h-screen w-full min-w-0">
        
        {/* TOP BAR */}
        <header className="fixed top-0 left-0 lg:left-64 right-0 z-30 bg-white lg:bg-surface/80 lg:backdrop-blur-md border-b border-slate-200 lg:border-outline-variant/10 flex items-center px-4 lg:px-6 h-14 lg:h-auto lg:py-3">
          
          {/* Single NotificationSystem instance — visible on all screen sizes via absolute positioning.
               Mobile: right-4 (nothing to the right).
               Desktop: right-[60px] to sit left of the profile button (~40px) + gap. */}
          <div className="absolute right-4 lg:right-[60px] top-0 h-14 lg:h-auto lg:top-auto flex items-center">
            {rightAction || <NotificationSystem />}
          </div>

          {/* Mobile header */}
          <div className="lg:hidden flex items-center flex-1">
            <div className="w-10">
              {showBack ? (
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 -ml-2 hover:bg-slate-100 rounded-full transition-colors"
                  aria-label="Go back"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-700" aria-hidden="true" />
                </button>
              ) : (
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="p-2 -ml-2 hover:bg-slate-100 rounded-full transition-colors"
                  aria-label="Open navigation menu"
                  aria-expanded={mobileMenuOpen}
                >
                  <Menu className="w-5 h-5 text-slate-700" aria-hidden="true" />
                </button>
              )}
            </div>
            <h1 className="font-bold text-lg text-slate-900 flex-1 text-center truncate">
              {title}
            </h1>
            {/* Bell is rendered once above — reserve space so title stays centered */}
            <div className="w-10" />
          </div>

          {/* Desktop header */}
          <div className="hidden lg:flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              {showBack && (
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-1.5 text-slate-500 hover:text-primary text-sm font-semibold transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <h1 className="font-bold text-lg text-slate-900">{title}</h1>
            </div>
            <div className="flex items-center gap-4">
              {/* Doctor search bar */}
              {role === 'DOCTOR' && (
                <div className="relative">
                  <input
                    className="bg-surface-container-low border-none rounded-full px-4 py-1.5 text-sm w-48 focus:ring-2 focus:ring-primary focus:w-64 transition-all placeholder:text-slate-500"
                    placeholder="Search patients..."
                    aria-label="Search patients and symptoms"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setSearchResults(null), 200)}
                  />
                  {searchLoading ? (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary animate-spin" />
                  ) : (
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  )}
                  {searchResults && (
                    <div className="absolute top-full mt-2 right-0 w-80 bg-white rounded-xl shadow-lg border border-outline-variant/10 py-2 max-h-96 overflow-y-auto z-50">
                      {searchResults.patients?.length > 0 && (
                        <>
                          <p className="px-4 py-1 text-xs font-extrabold text-slate-500 uppercase tracking-widest">Patients</p>
                          {searchResults.patients.map(p => (
                            <button key={p._id} onClick={() => { setSearchResults(null); setSearchQuery(''); navigate(`/patient/${p._id}`); }}
                              className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50">
                              <UserCircle className="w-4 h-4 text-primary" />
                              <span className="font-medium text-slate-900">{p.name}</span>
                              <span className="text-xs text-slate-500">{p.email}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {searchResults.symptoms?.length > 0 && (
                        <>
                          <p className="px-4 py-1 text-xs font-extrabold text-slate-500 uppercase tracking-widest mt-2">Symptoms</p>
                          {searchResults.symptoms.map(s => (
                            <button key={s._id} onClick={() => { setSearchResults(null); setSearchQuery(''); navigate(`/symptom/${s._id}`); }}
                              className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50">
                              <Activity className="w-4 h-4 text-secondary" />
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-slate-900 break-words block">{s.title}</span>
                                <span className="text-xs text-slate-500">{s.patient} · {s.severity}</span>
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                      {(!searchResults.patients?.length && !searchResults.symptoms?.length) && (
                        <p className="px-4 py-3 text-sm text-slate-500 text-center">No results found</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {role === 'USER' && (
                <Link to="/help" className="text-primary font-bold px-4 py-2 hover:bg-slate-100/50 rounded-full transition-colors text-sm flex items-center gap-1.5">
                  <HelpCircle className="w-4 h-4" />
                  Help
                </Link>
              )}
              {/* NotificationSystem is rendered once above (absolute-positioned), not duplicated here */}
              <div className="w-10" /> {/* spacer to offset the absolute bell */}
              <button onClick={() => navigate('/profile')} className="p-2 hover:bg-slate-100/50 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50" aria-label="Profile">
                <UserCircle className="w-5 h-5 text-slate-700" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile slide-out menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" aria-label="Navigation menu" style={{ animation: 'slideInLeft 0.2s ease-out' }}>
              {/* Menu header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-container flex items-center justify-center">
                    <Stethoscope className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-base font-black text-primary tracking-tighter">SymptoConnect</div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{portalLabel}</div>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                  aria-label="Close navigation menu"
                >
                  <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto py-2 px-3">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isNavActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left mb-1 ${
                        active
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                      {item.path === '/messages' && badgeCounts.messages > 0 && (
                        <span className="ml-auto bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {badgeCounts.messages > 99 ? '99+' : badgeCounts.messages}
                        </span>
                      )}
                      {item.path === '/care-requests' && role === 'USER' && pendingRequestsCount > 0 && (
                        <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                        </span>
                      )}
                      {item.path === '/health-log' && doctorCounts.patientLog > 0 && (
                        <span className="ml-auto bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {doctorCounts.patientLog > 99 ? '99+' : doctorCounts.patientLog}
                        </span>
                      )}
                      {item.path === '/care-requests' && role === 'DOCTOR' && doctorCounts.requests > 0 && (
                        <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {doctorCounts.requests > 99 ? '99+' : doctorCounts.requests}
                        </span>
                      )}
                      {item.path === '/admin/doctors' && adminCounts.pendingDoctors > 0 && (
                        <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {adminCounts.pendingDoctors > 99 ? '99+' : adminCounts.pendingDoctors}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* User card + logout */}
              <div className="border-t border-slate-100 p-4 mb-20">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=00478d&color=fff&size=40`}
                    alt={user?.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 break-words">{user?.name || 'User'}</div>
                    <div className="text-xs text-slate-500 font-medium">{roleLabel}</div>
                  </div>
                </div>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-red-50 text-red-600 rounded-xl font-bold text-sm hover:bg-red-100 transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  Log Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <section
          id="main-content"
          role="main"
          aria-label="Page content"
          className="pt-14 lg:pt-16 pb-24 lg:pb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full overflow-x-hidden"
        >
          {children}
        </section>

        {/* BOTTOM NAV (Mobile only) */}
        <BottomNav />
      </main>
    </div>
  );
};

export default AppLayout;
